import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { touchpointKeys } from "hooks/api/query-keys";
import { useRewriteTouchpointsWithWriter } from "hooks/api/use-touchpoints";

import type {
  RewriteTouchpointContentsDTO,
  RewriteTouchpointContentsRequest,
} from "generated/schemas";

import type { TouchpointListResult } from "utilities/api-mappers";
import {
  clearEmailWriterPollingSession,
  getEmailWriterPollingScopedTouchpointIds,
  hasEmailWriterPollingSession,
  markEmailWriterPollingStarted,
} from "utilities/email-writer-polling-session";

type QueueCreativesRewriteParams = {
  clientId: string;
  campaignId: string;
  requests: RewriteTouchpointContentsRequest[];
};

export type QueueCreativesRewriteResult = RewriteTouchpointContentsDTO & {
  failedRequestCount: number;
};

export const useQueueCreativesRewrite = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: rewriteTouchpoints, isPending: isQueueing } =
    useRewriteTouchpointsWithWriter();

  /**
   * Sends every request and starts completion polling for the union of queued creatives. The
   * polling session is a single per-campaign slot, so requests are batched here rather than each
   * caller marking it — a second mark would overwrite the first request's touchpoint ids. For the
   * same reason a rewrite already being polled in this campaign is folded into the new session.
   * Rejects only when every request failed; partial failures are reported in `failedRequestCount`
   * because the creatives queued by the other requests are already being written.
   */
  const queueRewrites = useCallback(
    async ({ clientId, campaignId, requests }: QueueCreativesRewriteParams): Promise<QueueCreativesRewriteResult> => {
      const hadActiveSession = hasEmailWriterPollingSession(campaignId, clientId);
      const previouslyScopedIds = getEmailWriterPollingScopedTouchpointIds(campaignId);

      if (!hadActiveSession) {
        markEmailWriterPollingStarted(campaignId, clientId);
      }

      const settled = await Promise.allSettled(
        requests.map((request) => rewriteTouchpoints({ clientId, campaignId, ...request })),
      );
      const fulfilled = settled.flatMap((result) => (result.status === "fulfilled" ? [result.value] : []));
      const queued = fulfilled.flatMap((result) => result.queued);
      const skipped = fulfilled.flatMap((result) => result.skipped);
      const failedRequestCount = settled.length - fulfilled.length;

      if (queued.length === 0) {
        if (!hadActiveSession) {
          clearEmailWriterPollingSession(campaignId);
        }
      } else {
        // An active session with no scoped ids is campaign-wide (create-with-writer); scoping it to
        // the new ids would stop polling the creatives that job is still writing.
        const keepsCampaignWideSession = hadActiveSession && !previouslyScopedIds?.length;

        markEmailWriterPollingStarted(
          campaignId,
          clientId,
          keepsCampaignWideSession
            ? undefined
            : {
                touchpointIds: Array.from(
                  new Set([...(previouslyScopedIds ?? []), ...queued.map((touchpoint) => touchpoint.id)]),
                ),
              },
        );

        const runningStatusByTouchpointId = new Map(
          queued.map((touchpoint) => [touchpoint.id, touchpoint.batchJobExecutionStatus]),
        );

        queryClient.setQueriesData<TouchpointListResult>(
          { queryKey: touchpointKeys.lists() },
          (data) =>
            data && {
              ...data,
              items: data.items.map((item) =>
                runningStatusByTouchpointId.has(item.id)
                  ? { ...item, batchJobExecutionStatus: runningStatusByTouchpointId.get(item.id) }
                  : item,
              ),
            },
        );
      }

      const rejected = settled.find((result) => result.status === "rejected");
      if (rejected && fulfilled.length === 0) {
        throw rejected.reason;
      }

      return { queued, skipped, failedRequestCount };
    },
    [queryClient, rewriteTouchpoints],
  );

  return { queueRewrites, isQueueing };
};
